import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useAuthModal, useGuardedNavigate } from "../../context/AuthModalContext";
import { useFloatingChat } from "../../context/FloatingChatContext";
import { useTranslation } from "../../context/TranslationContext";
import Badge from "../ds/Badge";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import IconButton, { STATE_LAYER } from "../ds/IconButton";
import { useDismiss } from "./useShellData";
import { conversationPreview, isGroupChat } from "../../utils/groupChats";
import { getProfileAvatarUrl } from "../../utils/userDisplay";
import { initialsOf } from "../ds/Avatar";

/*
  Handoff "Messages bar": a floating pill at the bottom end of every desktop
  screen — minimized chats and the newest ones, and the unread count. It opens
  the inbox above it; a chat opens in a floating window in the same spot. Back in
  that chat returns to the inbox, and the bar collapses it.
  Below 1024px chats live in the Tab bar instead.
*/

const otherOf = (conversation, userId) => conversation.participants?.find((p) => (p._id || p.id) !== userId);

/** A chat's picture and name. Groups have no picture of their own, so a member's stands in. */
const faceOf = (conversation, userId) => {
  if (!isGroupChat(conversation)) {
    const other = otherOf(conversation, userId);
    return { src: getProfileAvatarUrl(other), name: other?.username || other?.businessName };
  }
  const member = conversation.participants?.find((p) => (p._id || p.id) !== userId && getProfileAvatarUrl(p));
  return { src: getProfileAvatarUrl(member), name: conversation.name, isGroup: true };
};

const ChatFace = ({ conversation, userId, className = "" }) => {
  const { src, name, isGroup } = faceOf(conversation, userId);
  return (
    <span
      className={`flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-14 bg-fill-secondary text-caption-1-semibold text-label-secondary ${className}`}
      aria-hidden="true"
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : isGroup ? (
        <Icon name="users" size={16} />
      ) : (
        initialsOf(name, 1)
      )}
    </span>
  );
};

const ShellMessagesBar = ({ inbox }) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const navigate = useGuardedNavigate();
  const floatingChat = useFloatingChat();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("open-inbox", show);
    return () => window.removeEventListener("open-inbox", show);
  }, []);

  const userId = user?.id ?? user?._id;
  const { conversations, requests, unread } = inbox;
  // Minimized chats first, newest on top, then whoever wrote last.
  const minimized = (floatingChat?.windows || [])
    .filter((chat) => chat.minimized)
    .map((chat) => chat.conversation)
    .reverse();
  const faces = [...minimized, ...conversations.filter((c) => !minimized.some((m) => m._id === c._id))].slice(0, 3);
  const openChatId = floatingChat?.windows.find((chat) => !chat.minimized)?.conversation._id;

  const openChat = (conversation) => {
    close();
    if (conversation._id === openChatId) floatingChat.minimizeChat(openChatId);
    else if (floatingChat?.openChat) floatingChat.openChat(conversation);
    else navigate(`/messages/${conversation._id}`);
  };

  const toggle = () => {
    if (!isAuthenticated) openAuthModal({ form: "signin", redirectTo: "/messages" });
    else if (openChatId) floatingChat.minimizeChat(openChatId);
    else setOpen((o) => !o);
  };

  return (
    <div ref={ref} className="fixed bottom-space-24 right-space-24 z-[25] font-ds text-label-primary">
      {open && (
        <div
          role="dialog"
          aria-label={t("messages.inbox")}
          className="absolute bottom-[calc(100%+8px)] right-0 flex max-h-[min(560px,70vh)] w-[min(360px,calc(100vw-32px))] flex-col gap-space-4 overflow-y-auto rounded-radius-lg border border-separator-default bg-bg-overlay p-space-8 shadow-overlay animate-ds-pop-in"
        >
          <div className="flex items-center justify-between gap-space-8 px-space-8 pb-space-4 pt-space-8">
            <div className="flex items-center gap-space-8">
              <h2 className="text-headline">{t("messages.inbox")}</h2>
              {requests.length > 0 && (
                <Badge size="s" tone="danger">
                  {t("shell.requests", { count: requests.length })}
                </Badge>
              )}
            </div>
            <IconButton
              variant="plain"
              label={t("groups.newGroup")}
              onClick={() => {
                close();
                navigate("/messages?newGroup=1");
              }}
            >
              <Icon name="users" size={18} />
            </IconButton>
          </div>
          {conversations.length === 0 ? (
            <p className="px-space-8 py-space-16 text-footnote text-label-tertiary">{t("messages.noConversations")}</p>
          ) : (
            conversations.slice(0, 6).map((conversation) => {
              const other = otherOf(conversation, userId);
              const name = isGroupChat(conversation) ? conversation.name : other?.username || other?.businessName || "User";
              return (
                <button
                  key={conversation._id}
                  type="button"
                  onClick={() => openChat(conversation)}
                  className={`relative flex w-full items-center gap-space-12 rounded-control p-space-8 text-left ${STATE_LAYER}`}
                >
                  <ChatFace conversation={conversation} userId={userId} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className={`truncate ${conversation.unreadCount > 0 ? "text-subheadline-semibold" : "text-subheadline"}`}>{name}</span>
                    {conversation.lastMessage && (
                      <span className="truncate text-footnote text-label-secondary">{conversationPreview(conversation, userId, t)}</span>
                    )}
                  </span>
                  {conversation.unreadCount > 0 && (
                    <Badge size="s" tone="danger" className="min-w-[20px]">
                      {conversation.unreadCount}
                    </Badge>
                  )}
                </button>
              );
            })
          )}
          <Button
            variant="gray"
            size="m"
            fullWidth
            className="mt-space-4 shrink-0"
            onClick={() => {
              close();
              navigate(requests.length > 0 && conversations.length === 0 ? "/messages?requests=1" : "/messages");
            }}
          >
            {t("messages.viewAllMessages")}
          </Button>
        </div>
      )}
      <div className="relative flex items-center gap-space-12 rounded-full border border-separator-default bg-bg-raised py-space-12 pl-space-20 pr-space-12 shadow-overlay">
        <button
          type="button"
          aria-expanded={open || !!openChatId}
          aria-label={unread ? `${t("messages.messages")}, ${unread}` : t("messages.messages")}
          onClick={toggle}
          className={`absolute inset-0 rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${STATE_LAYER}`}
        />
        <Icon name="message" size={24} />
        <span className="text-headline">{t("messages.messages")}</span>
        {faces.length > 0 && (
          <span className="flex">
            {faces.map((conversation, i) => (
              <button
                key={conversation._id}
                type="button"
                title={faceOf(conversation, userId).name}
                aria-label={faceOf(conversation, userId).name}
                onClick={() => openChat(conversation)}
                className={`relative rounded-14 outline-none transition-transform hover:-translate-y-px focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
                  i > 0 ? "-ml-[10px]" : ""
                }`}
              >
                <ChatFace conversation={conversation} userId={userId} className="border-2 border-bg-raised" />
              </button>
            ))}
          </span>
        )}
        {unread > 0 && (
          <Badge size="s" tone="danger" className="min-w-[20px]">
            {unread > 99 ? "99+" : unread}
          </Badge>
        )}
      </div>
    </div>
  );
};

export default ShellMessagesBar;
